import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { NgbActiveModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TRANSACTION_TYPE_VALUES } from '../../../data/suscriptions-payments/constants';
import { environment } from '../../../../../environments/environment';
import { FinancialService } from '../../../../core/services/financial.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DynamicModalService } from '../../../../core/services/dynamic-modal.service';

interface DocumentItem {
  id_documento: number;
  nombre_documento: string;
  ruta: string;
  tipo: string;
  safeUrl?: SafeResourceUrl;
}

@Component({
  selector: 'app-forms-modal',
  templateUrl: './forms-modal.component.html',
  styleUrl: './forms-modal.component.scss',
})
export class FormsModalComponent implements OnInit {
  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef })
  dynamicComponentContainer!: ViewContainerRef;
  @Input() component: any;
  componentRef!: ComponentRef<any>;
  @Input() title!: string;
  @Input() isEditForm!: boolean;
  @Input() id_financiero!: string;
  @Input() tipoTransaccion!: number;
  @Input() fotoComprobante!: string;
  @Input() id_material!: number;
  @Input() is_edit_material!: boolean;

  public isChatOpen: boolean = false;
  public TYPE_TRANSACTION = TRANSACTION_TYPE_VALUES;
  public baseUrl: string = environment.API_FILES;
  public pdfReady: boolean = true;
  public isMinimized: boolean = false;

  // Carrusel de documentos
  public documents: DocumentItem[] = [];
  public currentIndex: number = 0;

  constructor(
    public activeModal: NgbActiveModal,
    config: NgbModalConfig,
    private resolver: ComponentFactoryResolver,
    private _financialService: FinancialService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private dynamicModalService: DynamicModalService,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {
    config.backdrop = 'static';
    config.keyboard = false;
  }

  showModal: boolean = false;

  ngAfterViewInit() {
    if (this.component) {
      const componentFactory = this.resolver.resolveComponentFactory(
        this.component
      );
      this.dynamicComponentContainer.clear();
      this.componentRef =
        this.dynamicComponentContainer.createComponent(componentFactory);

      if (this.componentRef.instance) {
        this.componentRef.instance.isEditForm = this.isEditForm;
        this.componentRef.instance.tipoTransaccion = this.tipoTransaccion;
        this.componentRef.instance.fotoComprobante = this.fotoComprobante;
        this.componentRef.instance.id_material = this.id_material;
        this.componentRef.instance.is_edit_material = this.is_edit_material;
        this.componentRef.instance.args = this.dynamicModalService.getModalData();
        this.componentRef.instance.modalHost = {
          minimize: () => this.minimizeModal(),
          restore: () => this.restoreModal(),
        };
      }
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    this.loadDocuments();
  }

  loadDocuments(): void {
    if (
      this.tipoTransaccion === TRANSACTION_TYPE_VALUES.FINANCIAL &&
      this.id_financiero
    ) {
      this._financialService
        .getFinancialMovement(this.id_financiero)
        .subscribe({
          next: (response) => {
            const docs = response.data.documents || [];

            this.documents = docs.map((doc: any) => {
              const cleanPath = doc.ruta
                .replace(/ /g, '%20')
                .replace(/\\/g, '/');
              const fullUrl = `${this.baseUrl}/${cleanPath}`;

              return {
                id_documento: doc.id_documento,
                nombre_documento:
                  doc.nombre_documento || this.extractFileName(doc.ruta),
                ruta: doc.ruta,
                tipo: doc.tipo,
                safeUrl: this.getSafeUrl(fullUrl),
              };
            });

            this.currentIndex = 0;
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Error loading documents:', err);
            this.documents = [];
          },
        });
    }
  }

  private extractFileName(path: string): string {
    const parts = path.split(/[/\\]/);
    return parts[parts.length - 1] || 'documento';
  }

  getSafeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  isPdf(doc: DocumentItem): boolean {
    if (!doc) return false;
    return (
      doc.tipo?.toLowerCase().includes('pdf') ||
      doc.ruta?.toLowerCase().endsWith('.pdf') ||
      false
    );
  }

  prevDocument(): void {
    if (this.currentIndex > 0) {
      this.goToDocument(this.currentIndex - 1);
    }
  }

  nextDocument(): void {
    if (this.currentIndex < this.documents.length - 1) {
      this.goToDocument(this.currentIndex + 1);
    }
  }

  goToDocument(index: number): void {
    if (index >= 0 && index < this.documents.length) {
      if (
        this.isPdf(this.documents[index]) &&
        !this.isPdf(this.documents[this.currentIndex])
      ) {
        this.pdfReady = false;
        this.currentIndex = index;
        setTimeout(() => {
          this.pdfReady = true;
          this.cdr.detectChanges();
        }, 350);
      } else {
        this.currentIndex = index;
      }
    }
  }

  openFullscreen(): void {
    const doc = this.documents[this.currentIndex];
    if (doc && !this.isPdf(doc)) {
      const cleanPath = doc.ruta.replace(/ /g, '%20').replace(/\\/g, '/');
      const fullUrl = `${this.baseUrl}/${cleanPath}`;
      window.open(fullUrl, '_blank');
    }
  }

  downloadDocument(): void {
    const doc = this.documents[this.currentIndex];
    if (doc) {
      const cleanPath = doc.ruta.replace(/ /g, '%20').replace(/\\/g, '/');
      const fullUrl = `${this.baseUrl}/${cleanPath}`;

      fetch(fullUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = doc.nombre_documento;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error('Error al descargar:', error);
          window.open(fullUrl, '_blank');
        });
    }
  }

  openModal(): void {
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  minimizeModal(): void {
    this.isMinimized = true;
    this.toggleMinimizedClasses(true);
    this.renderer.removeClass(document.body, 'modal-open');
    this.dynamicModalService.updateModalState(this.title, true, true);
    this.cdr.detectChanges();
  }

  restoreModal(): void {
    this.isMinimized = false;
    this.toggleMinimizedClasses(false);
    this.renderer.addClass(document.body, 'modal-open');
    this.dynamicModalService.updateModalState(this.title, true, false);
    this.cdr.detectChanges();
  }

  closingModal(): void {
    this.dynamicModalService.closeModal();
  }

  toggleChat(): void {
    this.isChatOpen = !this.isChatOpen;

    if (
      this.isChatOpen &&
      this.documents.length > 0 &&
      this.isPdf(this.documents[this.currentIndex])
    ) {
      this.pdfReady = false;
      setTimeout(() => {
        this.pdfReady = true;
        this.cdr.detectChanges();
      }, 350);
    }
  }

  ngOnDestroy(): void {
    this.toggleMinimizedClasses(false);
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  private toggleMinimizedClasses(isMinimized: boolean): void {
    const modalWindow = this.elementRef.nativeElement.closest('.modal');
    const modalDialog =
      this.elementRef.nativeElement.closest('.modal-dialog');
    const modalContent =
      this.elementRef.nativeElement.closest('.modal-content');
    const backdrops = document.querySelectorAll('.modal-backdrop');
    const latestBackdrop = backdrops[backdrops.length - 1];

    if (modalWindow) {
      this.renderer[isMinimized ? 'addClass' : 'removeClass'](
        modalWindow,
        'forms-modal-window--minimized'
      );
    }

    if (modalDialog) {
      this.renderer[isMinimized ? 'addClass' : 'removeClass'](
        modalDialog,
        'forms-modal-dialog--minimized'
      );
    }

    if (modalContent) {
      this.renderer[isMinimized ? 'addClass' : 'removeClass'](
        modalContent,
        'forms-modal-content--minimized'
      );
    }

    if (latestBackdrop) {
      this.renderer[isMinimized ? 'addClass' : 'removeClass'](
        latestBackdrop,
        'forms-modal-backdrop--minimized'
      );
    }
  }
}
